import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { keyframes } from '@mui/material/styles'
import { Add, ArrowUpward, AutoAwesome, ContentCopy, Fullscreen, FullscreenExit, KeyboardArrowDown, People } from '@mui/icons-material'
import { useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useChat } from '@/hooks/useChat'
import { useConversations } from '@/services/api/chat'
import { resumableConversationId } from '@/lib/chatResume'
import { flowChatBus } from '@/lib/flowChatBus'
import { clearFlowKickoff, peekFlowKickoff } from '@/lib/flowOnboardingKickoff'
import { useLongPress } from '@/hooks/useLongPress'
import { useAthlete } from '@/contexts/AthleteContext'
import { useAuth } from '@/contexts/AuthContext'
import { useNavbar } from '@/contexts/NavbarContext'
import ChatPanel from './ChatPanel'
import ConversationList from './ConversationList'
import RoutineSidebarSection from './RoutineSidebarSection'
import { beoflowColors, borderRadius, layout, opacity, spacing, typography } from '@/theme/muiTheme'

const SIDEBAR_WIDTH = 300

interface FlowChatLauncherProps {
  /** A program as the conversation subject (coach onboarding's program
   *  calendar). Flow's plan edits then refresh the calendar, and the launcher
   *  starts a fresh thread instead of resuming an unrelated one. */
  planId?: number
  /** Open on mount, e.g. a door where the coach's first move is to talk to Flow. */
  defaultOpen?: boolean
  /** A page that is introducing Flow: its own headline, prompts and composer
   *  text for the empty thread, and a brief glow when the panel first opens. */
  intro?: { title: string; prompts?: string[]; placeholder?: string }
}

// The floating surfaces' one shadow: soft and neutral, separation not drama.
const FLOAT_SHADOW = '0 12px 32px rgba(30, 30, 30, 0.10)'

// Three soft pulses, then still: enough to be noticed, not enough to nag.
const introGlow = keyframes`
  0%, 100% { box-shadow: ${FLOAT_SHADOW}, 0 0 0 0 ${beoflowColors.meanBlue}00; }
  50% { box-shadow: ${FLOAT_SHADOW}, 0 0 0 10px ${beoflowColors.meanBlue}55; }
`

export default function FlowChatLauncher({ planId, defaultOpen = false, intro }: FlowChatLauncherProps = {}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  // A detail drawer open on the right (the calendar's session panel) covers
  // this corner; step aside rather than sit under it.
  const { panelRightOffset } = useNavbar()
  const rightInset = (base: string) => (isMobile || !panelRightOffset ? base : `calc(${base} + ${panelRightOffset}px)`)
  const location = useLocation()
  const queryClient = useQueryClient()
  // On a dashboard (/views/:id) the Flow chat targets that view: its id is
  // injected into the turn (backend) and a widget/view mutation refetches the
  // grid. Off a dashboard, viewId is undefined and Flow is the general expert.
  const viewMatch = location.pathname.match(/^\/views\/(\d+)/)
  const viewId = viewMatch ? Number(viewMatch[1]) : undefined
  // On the team overview with one team picked (/team?group=<uuid>), Flow
  // inherits that team: the backend resolves its squad from group_uuid.
  const onTeamOverview = location.pathname === '/team'
  const groupUuid = onTeamOverview
    ? new URLSearchParams(location.search).get('group') ?? undefined
    : undefined
  const onViewMutated = useCallback(() => {
    if (viewId) queryClient.invalidateQueries({ queryKey: ['views', viewId] })
  }, [queryClient, viewId])
  const onPlanMutated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['activity-events'] })
  }, [queryClient])
  // Onboarding lands here after the wizard (navigate('/calendar', { state:
  // { openFlow: true } })); the launcher mounts fresh, so open Flow from the
  // start to help the athlete get going.
  const launchState = location.state as { openFlow?: boolean; flowKickoff?: string } | null
  // Flow is not presentable on these routes, so the launcher renders nothing
  // (see the early return below). Hoisted above the stored-kickoff read because
  // consuming it here would DESTROY a pending kickoff on a page that could never
  // have shown it.
  const flowSuppressed =
    location.pathname === '/chat' ||
    location.pathname === '/access' ||
    location.pathname.startsWith('/workouts') ||
    location.pathname.startsWith('/activity')

  // A stored kickoff is the SAME intent as `location.state`, minus the
  // fragility. Any redirect between the completion navigate and this mount
  // discards `location.state` silently, and that is exactly what happened on
  // staging (2026-09-15): the athlete arrived at /calendar twice and the second
  // arrival carried nothing, so Flow stayed shut.
  //
  // PEEK, never consume here. Reading-and-clearing at mount was the obvious
  // design and it lost the kickoff: the first mount ate it, the tree then
  // remounted (the route is replaced with no state, and StrictMode remounts in
  // development anyway), and the surviving mount found an empty slot. The value
  // is cleared where it is acted on, in the effect below.
  const storedKickoff = flowSuppressed ? null : peekFlowKickoff()
  const pendingKickoff = launchState?.flowKickoff || storedKickoff || null
  const [isOpen, setIsOpen] = useState(() => defaultOpen || Boolean(launchState?.openFlow) || Boolean(storedKickoff))
  const [isMaximized, setIsMaximized] = useState(false)
  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [copyStatus, setCopyStatus] = useState<string | null>(null)
  const athlete = useAthlete()
  const { user } = useAuth()
  // On the team overview Flow is the COACH's, scoped to the team in view. The
  // athlete selection persists app-wide and the team page hides its chip, so a
  // coach who last opened an athlete was silently chatting about them here.
  const targetProfileId = onTeamOverview ? null : athlete.selectedProfileId
  const subjectName = onTeamOverview ? null : athlete.selectedProfileName
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser)
  const composerPlaceholder = intro?.placeholder ?? (subjectName ? `Ask about ${subjectName}'s training...` : 'Ask about your health...')
  const closedLabel = composerPlaceholder.replace(/\.\.\.$/, '')

  const {
    messages,
    conversationId,
    isStreaming,
    error,
    messagesQuery,
    sendMessage,
    regenerateLast,
    canRegenerateLast,
    stopStreaming,
    loadConversation,
    hydrateMessages,
    newConversation,
    dismissError,
    settledProfileId,
  } = useChat(targetProfileId, { viewId, onViewMutated, planId, groupUuid, onPlanMutated: planId ? onPlanMutated : undefined })

  useEffect(() => {
    if (messagesQuery.data) {
      hydrateMessages(messagesQuery.data)
    }
  }, [messagesQuery.data, hydrateMessages])

  // Resume the athlete's current thread instead of opening blank, matching iOS.
  // The launcher mounts on every page, so this fires often — hence the recency
  // bound in resumableConversationId, and hence the ref.
  //
  // The ref is stamped ONLY on an actual resume, which is what makes the rest
  // safe to re-evaluate. Stamping on every pass was wrong twice (both caught by
  // cross-model review, both with failing tests): on an athlete switch the
  // effect sees the PREVIOUS athlete's conversationId in that commit, so it
  // bailed, stamped, and never resumed the new athlete's thread; and with the
  // conversations query resolving mid-turn it called loadConversation while a
  // turn was streaming, aborting the athlete's own live request.
  const { data: launcherConversations } = useConversations(targetProfileId)
  const resumedForRef = useRef<number | null | undefined>(undefined)
  useEffect(() => {
    if (resumedForRef.current === targetProfileId) return
    if (!launcherConversations) return
    // A program-scoped chat is about this program: never resume another thread into it.
    if (planId) return
    // Never disturb live or already-loaded content. Both also cover the athlete
    // switch mid-settle, where conversationId is still the previous athlete's.
    if (isStreaming || messages.length > 0) return
    // A kickoff is about to speak into a fresh thread, and a deep link or a
    // click already chose one. Neither wants a resume.
    if (pendingKickoff || conversationId !== null) return
    const resumeId = resumableConversationId(launcherConversations)
    if (resumeId === null) return
    resumedForRef.current = targetProfileId
    loadConversation(resumeId)
  }, [
    launcherConversations, targetProfileId, conversationId, isStreaming,
    messages.length, pendingKickoff, loadConversation, planId,
  ])

  // Post-onboarding handoff: send the kickoff so Flow speaks first. Opening the
  // panel runs no turn, and the backend only injects the onboarding directive
  // while a turn is running — which is why Flow used to open silent. iOS does
  // the same thing via `autoSendMessage`.
  //
  // Guarded by a ref, not by state: this must fire exactly once per navigation,
  // and `sendMessage` changes identity as the chat hydrates.
  const kickoffSent = useRef(false)
  const kickoffForRef = useRef<number | null | undefined>(undefined)
  useEffect(() => {
    const kickoff = pendingKickoff
    if (!kickoff || kickoffSent.current || messagesQuery.isLoading) return
    // Bind the kickoff to the athlete it was raised for. If the viewed athlete
    // CHANGES between this effect first seeing the kickoff and actually sending
    // it — a staff switch landing while messagesQuery is still loading — the
    // deps re-fire against the new athlete's empty messages and would post
    // "Just finished setting up my profile." into THEIR thread, attributed to
    // them. Keyed on a change rather than on `subjectName` being unset, because
    // the athlete selection is persisted and a stale one would otherwise block
    // a legitimate kickoff outright. Cross-model review, 2026-09-15; the resume
    // effect above already reasons about the same mid-settle switch.
    if (kickoffForRef.current === undefined) kickoffForRef.current = targetProfileId
    if (kickoffForRef.current !== targetProfileId) return
    // Never speak into an existing conversation: the directive is gated on the
    // athlete having no other conversation, and a stray kickoff in a real
    // thread reads as the athlete saying something they never said.
    if (messages.length > 0) {
      kickoffSent.current = true
      clearFlowKickoff()
      return
    }
    kickoffSent.current = true
    // Clear FIRST: this is the point at which the intent has been acted on, and
    // leaving it would let a later page repeat the sentence.
    clearFlowKickoff()
    sendMessage(kickoff)
  }, [pendingKickoff, messages.length, messagesQuery.isLoading, sendMessage, targetProfileId])

  // Let any page open Flow (and optionally send a prompt), e.g. the dashboard
  // grid's "fix this widget" shortcut. See flowChatBus.
  // Registered with the athlete the chat is settled for (undefined mid-switch),
  // so a prompt meant for one athlete is only ever sent from their chat.
  const settledFor = settledProfileId === targetProfileId ? targetProfileId : undefined
  useEffect(() => flowChatBus.register((prompt) => {
    setIsOpen(true)
    if (prompt) {
      sendMessage(prompt)
      flowChatBus.notifySent()
    }
  }, () => setIsOpen(false), settledFor), [sendMessage, settledFor])

  const handleNewConversation = useCallback(() => {
    // Asking for a blank chat is a decision about which thread to be in, so the
    // resume must not reopen the one just left — which it otherwise would, since
    // New Chat returns the hook to exactly the state resume looks for.
    resumedForRef.current = targetProfileId
    newConversation()
    setIsOpen(true)
    if (isMobile) setIsMaximized(false)
  }, [newConversation, isMobile, targetProfileId])

  const handleSelectConversation = useCallback((id: number) => {
    loadConversation(id)
    if (isMobile) setIsMaximized(false)
  }, [loadConversation, isMobile])

  const handleOpenCopyDialog = useCallback(() => {
    if (!isAdmin || isStreaming) return
    setCopyStatus(null)
    setCopyDialogOpen(true)
  }, [isAdmin, isStreaming])

  const flowTextLongPress = useLongPress({ onLongPress: handleOpenCopyDialog, delay: 650 })

  const buildConversationDetails = useCallback(() => {
    return [
      '# Flow conversation debug handoff',
      '',
      `conversation_id: ${conversationId ?? 'none'}`,
      `user_email: ${user?.email ?? 'unknown'}`,
      `user_profile_id: ${user?.profile_id ?? 'unknown'}`,
      `target_profile_id: ${targetProfileId ?? 'self'}`,
      `subject_name: ${subjectName ?? 'self'}`,
      `is_staff: ${Boolean(user?.is_staff)}`,
      `is_superuser: ${Boolean(user?.is_superuser)}`,
      `message_count_visible: ${messages.length}`,
      `current_url: ${window.location.href}`,
      `copied_at: ${new Date().toISOString()}`,
      '',
      'Use conversation_id to pull the full Conversation and ChatMessage rows from the DB.',
    ].join('\n')
  }, [conversationId, messages.length, subjectName, targetProfileId, user?.email, user?.is_staff, user?.is_superuser, user?.profile_id])

  const handleCopyConversation = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(buildConversationDetails())
      setCopyStatus('Copied conversation details to clipboard.')
    } catch (err) {
      setCopyStatus((err as Error).message || 'Copy failed')
    }
  }, [buildConversationDetails])

  if (flowSuppressed) return null

  return (
    <>
      {isOpen && (
        <Box
          data-tour="flow-panel"
          sx={{
            position: 'fixed',
            right: { xs: spacing.md, md: rightInset(spacing.xl) },
            transition: 'right 225ms',
            ...(intro && !reduceMotion ? { animation: `${introGlow} 1.6s ease-in-out 0.4s 3` } : {}),
            bottom: { xs: spacing.md, md: isMaximized ? spacing.xl : spacing.xl },
            width: isMaximized
              ? { xs: `calc(100vw - ${spacing.xxl})`, md: 'min(960px, calc(100vw - 40px))' }
              : { xs: `calc(100vw - ${spacing.xxl})`, md: '420px' },
            height: isMaximized
              ? { xs: `calc(100dvh - ${spacing.xxl})`, md: 'min(760px, calc(100dvh - 40px))' }
              : { xs: `calc(100dvh - ${spacing.xxl})`, md: 'min(720px, calc(100dvh - 40px))' },
            maxWidth: '100vw',
            zIndex: theme.zIndex.modal,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            // Chat mode in a card (Drafted 06-flow-launcher-styles, V1): paper,
            // the hairline chat mode's composer uses, one soft shadow.
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: { xs: borderRadius.xlarge, md: '22px' },
            boxShadow: FLOAT_SHADOW,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              px: spacing.md,
              py: spacing.sm,
              borderBottom: `${layout.borderDefault} solid ${beoflowColors.beoWhite}${opacity.separator}`,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: layout.chatAvatarSize,
                height: layout.chatAvatarSize,
                borderRadius: borderRadius.round,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                border: 'none',
              }}
            >
              <AutoAwesome sx={{ fontSize: layout.iconSmall, color: beoflowColors.beoBlack }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.xs, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  {...(isAdmin ? flowTextLongPress : {})}
                  onContextMenu={e => {
                    if (!isAdmin) return
                    e.preventDefault()
                    handleOpenCopyDialog()
                  }}
                  title={isAdmin ? 'Long press to copy this conversation for QA' : undefined}
                  sx={{
                    fontWeight: typography.fontWeightSemibold,
                    lineHeight: 1.2,
                    cursor: isAdmin && conversationId ? 'copy' : 'default',
                    userSelect: 'none',
                  }}
                >
                  Flow
                </Typography>
                {isAdmin && !isStreaming && (
                  <IconButton
                    onClick={handleOpenCopyDialog}
                    size="small"
                    aria-label="Copy Flow conversation for QA"
                    title="Copy Flow conversation for QA"
                    sx={{ color: 'text.secondary', p: '2px' }}
                  >
                    <ContentCopy sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
                {subjectName && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      px: spacing.sm,
                      py: '4px',
                      borderRadius: borderRadius.round,
                      backgroundColor: beoflowColors.meanBlue,
                      maxWidth: 180,
                      minWidth: 0,
                    }}
                  >
                    <People sx={{ fontSize: 14, color: beoflowColors.beoWhite, flexShrink: 0 }} />
                    <Typography
                      noWrap
                      sx={{
                        fontFamily: typography.fontFamilyMonospace,
                        fontSize: typography.fontSizeTiny,
                        fontWeight: typography.fontWeightSemibold,
                        textTransform: 'uppercase',
                        letterSpacing: typography.letterButton,
                        color: beoflowColors.beoWhite,
                        minWidth: 0,
                      }}
                    >
                      {subjectName}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography variant="monoSmall" sx={{ color: 'text.secondary' }}>
                {planId ? 'This program' : subjectName ? 'Viewing athlete data' : 'Viewing your data'}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setIsMaximized(prev => !prev)}
              size="small"
              aria-label={isMaximized ? 'Restore Flow chat' : 'Maximize Flow chat'}
              sx={{ color: 'text.secondary' }}
            >
              {isMaximized ? (
                <FullscreenExit sx={{ fontSize: layout.iconMedium }} />
              ) : (
                <Fullscreen sx={{ fontSize: layout.iconMedium }} />
              )}
            </IconButton>
            <IconButton
              onClick={() => setIsOpen(false)}
              size="small"
              aria-label="Minimize Flow chat"
              sx={{ color: 'text.secondary' }}
            >
              <KeyboardArrowDown sx={{ fontSize: layout.iconMedium }} />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
            {isMaximized && (
              <Box
                sx={{
                  width: isMobile ? '100%' : SIDEBAR_WIDTH,
                  flexShrink: 0,
                  borderRight: isMobile ? 'none' : `${layout.borderDefault} solid ${beoflowColors.beoWhite}${opacity.separator}`,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
              >
                <RoutineSidebarSection targetProfileId={targetProfileId} />
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: spacing.md,
                      py: spacing.sm,
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="monoLabel" sx={{ color: 'text.secondary' }}>
                      {subjectName ? `Conversations for ${subjectName}` : 'Conversations'}
                    </Typography>
                    <IconButton
                      onClick={handleNewConversation}
                      size="small"
                      aria-label="New Flow chat"
                      sx={{ color: beoflowColors.meanBlue }}
                    >
                      <Add sx={{ fontSize: layout.iconSmall }} />
                    </IconButton>
                  </Box>
                  <ConversationList
                    activeId={conversationId}
                    targetProfileId={targetProfileId}
                    emptyLabel={subjectName ? `No previous conversations for ${subjectName} yet.` : 'No previous conversations yet.'}
                    hideActive={isMobile}
                    onSelect={handleSelectConversation}
                  />
                </Box>
              </Box>
            )}
            {(!isMobile || !isMaximized) && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ChatPanel
                messages={messages}
                isStreaming={isStreaming}
                error={error}
                onSend={(...args) => { sendMessage(...args); flowChatBus.notifySent() }}
                onStop={stopStreaming}
                onDismissError={dismissError}
                onRegenerate={canRegenerateLast ? regenerateLast : undefined}
                placeholder={composerPlaceholder}
                emptyTitle={intro?.title}
                suggestedPrompts={intro?.prompts}
                enableAttachments
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Copy Flow conversation for QA</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: spacing.sm, color: 'text.secondary' }}>
            This copies the current visible conversation details to your clipboard. Paste it to me to start debugging.
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: spacing.sm,
              borderRadius: borderRadius.small,
              backgroundColor: 'rgba(0,0,0,0.12)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: typography.fontFamilyMonospace,
              fontSize: typography.fontSizeTiny,
            }}
          >
            {buildConversationDetails()}
          </Box>
          {copyStatus && (
            <Typography variant="body2" sx={{ mt: spacing.sm, color: 'text.secondary' }}>
              {copyStatus}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setCopyDialogOpen(false)}>
            Close
          </Button>
          <Button variant="contained" onClick={handleCopyConversation} disabled={!conversationId}>
            Copy
          </Button>
        </DialogActions>
      </Dialog>

      {!isOpen && (
        // Closed, Flow is the chat-mode composer itself (Drafted
        // 06-flow-launcher-styles, V2): the invitation reads as words, so a
        // first-time user learns what Flow is for before opening it.
        <Box
          component="button"
          type="button"
          onClick={() => setIsOpen(true)}
          aria-label="Open Flow chat"
          sx={{
            position: 'fixed',
            right: { xs: spacing.lg, md: rightInset(spacing.xl) },
            bottom: { xs: spacing.lg, md: spacing.xl },
            zIndex: theme.zIndex.speedDial,
            transition: 'right 225ms, border-color 150ms',
            height: 48,
            width: { xs: `min(300px, calc(100vw - 2 * ${spacing.lg}))`, md: 320 },
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm,
            pl: spacing.md,
            pr: '8px',
            cursor: 'pointer',
            font: 'inherit',
            textAlign: 'left',
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '22px',
            boxShadow: FLOAT_SHADOW,
            '&:hover': { borderColor: beoflowColors.meanBlue },
          }}
        >
          <AutoAwesome sx={{ fontSize: 20, color: beoflowColors.meanBlue }} />
          <Typography component="span" sx={{ flex: 1, minWidth: 0, fontSize: typography.fontSizeSm, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {closedLabel}
          </Typography>
          <Box component="span" sx={{ width: 32, height: 32, flexShrink: 0, borderRadius: borderRadius.round, backgroundColor: beoflowColors.meanBlue, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ArrowUpward sx={{ fontSize: 16, color: '#FFFFFF' }} />
          </Box>
        </Box>
      )}
    </>
  )
}
